import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  HttpClient,
  HttpHeaders
} from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { v4 as uuidv4 } from 'uuid';
import { lastValueFrom } from 'rxjs';
import { SessionData, GlobalErrorReason, MemberInfo, Identity } from './session-data';

export const AUTH_HEADER_KEY = 'Authorization';
export const AUTH_PREFIX = 'Bearer';
export const SET_AUTH_HEADER_KEY = 'Set-Authorization';

export const TOKEN_NAME = 'sessionData.token';
export const CORRELATION_ID = 'sessionData.correlationId';

export interface ConfigurationData {
  endpoints?: [{
    name: string;
    url: string;
  }];
  googleApiKey: any;
  tealium?: {
    enabled: boolean;
    account: string;
    environment: string;
    profile: string;
  };
  accertify?: {
    enabled: boolean;
    url: string;
  };
  optimizely?: { enabled: string; };
  appInsightsInstrumentationKey: string;
  statsig?: { clientKey: string; };
  applicationEnvironment?: { tier: string; };
}

export interface AuthData {
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  constructor(
    private httpClient: HttpClient,
    @Inject(DOCUMENT) private document: Document
  ) { }

  private sessionData: SessionData;
  private displayOptions: any;
  private featureFlags: any;
  private configData: ConfigurationData;

  public getSessionData(): SessionData {
    return this.sessionData;
  }

  public getDisplayOptions() {
    return this.displayOptions;
  }

  public getConfigData(): ConfigurationData {
    return this.configData;
  }

  public getFeatureFlags() {
    return this.featureFlags;
  }

  public async loadConfig(): Promise<any> {

    try {
      const r = await this.fetchConfiguration();
      const r_1 = await this.fetchToken(r);
      const r_2 = await this.fetchSessionData(r_1);
      return this.applyStyles(r_2);
    } catch (message) {
      return console.warn(message);
    }
  }

  private fetchConfiguration(): Promise<void | SessionData> {
    return lastValueFrom(this.httpClient.get<ConfigurationData>('api/configuration'))
      .then(configData => {

        if (!configData.endpoints) {
          this.sessionDataErrorInit();
          throw new Error('Configuration has no endpoints');
        }
        this.configData = configData;

        return new SessionData(
          this.getCorrelationId(),
          this.getMemberId(),
          this.getThirdPartyId(),
          this.getThirdPartyGuid(),
          this.getTransactionType(),
          this.getAgentId(),
          this.getPartnerId(),
          this.getNameId(), null, [], [],
          configData.endpoints, configData.accertify, null, configData.googleApiKey, null,
          null,
          null,
          null,
          null,
          null,
          null,
          this.getLoginToken(),
          null,
          null,
          this.getPlatform(),
          this.getMemberClassId()
        );
      }, reason => {
        this.sessionDataErrorInit(reason.status);
      });
  }

  private fetchToken(sessionData: SessionData | void): Promise<void | SessionData> {
    if (!sessionData) {
      return Promise.resolve();
    }

    // try to restore token if needed
    if (
      !sessionData.token &&
      !sessionData.nameId &&
      !sessionData.partnerId &&
      !sessionData.loginToken) {

      const token = getAuthToken();
      sessionData.token = token;

      if (!sessionData.token) {
        this.sessionDataErrorInit();
      }

      const promise = new Promise<SessionData>((resolve) => {
        resolve(sessionData);
      });

      return promise;
    }

    const endpoint = sessionData.endpoints.find(v => v.name === 'WebApi');
    const bodyObject = {
      nameId: sessionData.nameId,
      partnerId: sessionData.partnerId,
      agentId: sessionData.agentId,
      memberId: sessionData.memberId,
      thirdPartyId: sessionData.thirdPartyId,
      thirdPartyGuid: sessionData.thirdPartyGuid,
      transactionType: sessionData.transactionType,
      loginToken: sessionData.loginToken,
      platform: sessionData.platform,
      memberclassId: sessionData.memberclassid
    };

    return lastValueFrom(this.httpClient
      .post<AuthData>(endpoint.url + '/account/token', bodyObject))
      .then(response => {
        const token = response.token;
        setAuthToken(token);
        sessionData.token = token;

        this.initCorrelationId(sessionData);

        if (sessionData.loginToken) {
          this.loadSessionDataFromToken(sessionData, token);
        }
        return sessionData;
      }, reason => this.sessionDataErrorInit(reason.status));

  }

  private initCorrelationId(sessionData: any) {
    const correlationId = uuidv4();
    this.setCorrelationId(correlationId);
    sessionData.correlationId = correlationId;
  }

  public fetchTokenForAuthenticatedUser(sessionData: SessionData): Promise<void | SessionData> {
    const endpoint = sessionData.endpoints.find(v => v.name === 'WebApi');
    const bodyObject = {
      nameId: sessionData.nameId,
      partnerId: sessionData.partnerId,
      agentId: sessionData.agentId,
      memberId: sessionData.memberId,
      thirdPartyId: sessionData.thirdPartyId,
      thirdPartyGuid: sessionData.thirdPartyGuid,
      transactionType: sessionData.transactionType,
      loginToken: sessionData.loginToken,
      platform: sessionData.platform
    };

    return lastValueFrom(this.httpClient
      .post<AuthData>(endpoint.url + '/account/tokenForAuthenticatedUser', bodyObject))
      .then(response => {
        const token = response.token;
        setAuthToken(token);
        sessionData.token = token;

        this.initCorrelationId(sessionData);

        if (sessionData.loginToken) {
          this.loadSessionDataFromToken(sessionData, token);
        }
        return sessionData;
      }, reason => this.sessionDataErrorInit(reason.status));

  }

  private loadSessionDataFromToken(sessionData: SessionData, token: string) {
    var jwt = new JwtHelperService();
    var user = jwt.decodeToken(token);
    sessionData.nameId = user.NameId || null;
    sessionData.partnerId = user.PartnerId || null;
    sessionData.agentId = user.AgentId || null;
    sessionData.memberId = user.MemberId || null;
    sessionData.thirdPartyId = user.ThirdPartyId || null;
    sessionData.thirdPartyGuid = user.ThirdPartyGuid || null;
    sessionData.transactionType = user.TransactionType || sessionData.transactionType;
    sessionData.isAuthenticated = user.IsAuthenticated === "true";
    sessionData.platform = user.Platform;
  }

  private sessionDataErrorInit(reason?: GlobalErrorReason) {
    // we have no settings + app is still not started

    // set empty session Data to start app and show error
    if (!this.sessionData ||
      !this.sessionData.endpoints ||
      this.sessionData.endpoints.length === 0) {
      this.sessionData = new SessionData();
    }

    // trigger error to catch it on any (first) route
    // to redirect on special error page route instead
    this.sessionData.globalErrorReason = reason;
    this.sessionData.globalErrorTriggered = true;
    this.sessionData.partnerId = this.getPartnerId();
    this.sessionData.thirdPartyGuid = this.getThirdPartyGuid();
  }

  private fetchSessionData(sessionData: SessionData | void): Promise<void | SessionData> {

    if (!sessionData) {
      return Promise.resolve();
    }

    if (!sessionData.endpoints) {
      this.sessionDataErrorInit();
      throw new Error('Session Data has no endpoints');
    }

    const endpoint = sessionData.endpoints.find(v => v.name === 'WebApi');

    const headers = new HttpHeaders()
      .set(AUTH_HEADER_KEY, getAuthTokenAndPrefix(sessionData.token));

    return lastValueFrom(this.httpClient
      .get<any>(endpoint.url + '/settings', { headers: headers }))
      .then(resp => {

        const response = resp;
        this.displayOptions = response.display_options;
        this.displayOptions.optionsId = response.options_id;
        this.featureFlags = response.feature_flags;
        sessionData.acceptedCards = response.accepted_cards || [];

        if (this.displayOptions) {
          if (this.displayOptions.templates) {
            sessionData.templatesUrls =
              Object.values(this.displayOptions.templates);
          }

          if (this.displayOptions.stylesheets) {
            sessionData.stylesheetsUrls =
              this.displayOptions.stylesheets &&
                this.displayOptions.stylesheets.car
                ? [this.displayOptions.stylesheets.car]
                : [];
          }

          if (sessionData.stylesheetsUrls && this.displayOptions.stylesheets.sor_club_variables) {
            sessionData.stylesheetsUrls = [this.displayOptions.stylesheets.sor_club_variables, ...sessionData.stylesheetsUrls];
          }
        }

        sessionData.memberInfo = Object.assign(new MemberInfo(), response.member_info);

        const token = getAuthToken();

        if (token) {
          sessionData.token = token;
        }

        sessionData.isAgent =
          !!(response.identity && response.identity.agent_mode);

        sessionData.identity = Object.assign(new Identity(), response.identity);
        sessionData.optionsId = response.options_id;

        this.sessionData = sessionData;
        return sessionData;
      }, reason => this.sessionDataErrorInit(reason.status));
  }

  private applyStyles(sessionData: SessionData | void) {

    if (!sessionData ||
      !sessionData.stylesheetsUrls ||
      sessionData.stylesheetsUrls.length === 0) {
      return;
    }

    const head = this.document.getElementsByTagName('head');
    if (head && head[0]) {
      /*
        // remove default styles
        const styles = Array.from(head[0].children)
          .filter(v => v['getAttribute']('rel') === 'stylesheet');
        styles.forEach(element => {
          const htmlElement = (<HTMLElement>element);
          head[0].removeChild(htmlElement);
        });
      */
      // add new styles
      sessionData.stylesheetsUrls.forEach(styleUrl => {
        // create 'link'
        const link = document.createElement('link');
        // link.id = sessionData
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.href = styleUrl;
        link.media = 'all';

        head[0].appendChild(link);
      });
    }
  }

  public getNameId(): string {
    return this.getParameterValue('nameid');
  }

  public getPartnerId(): string {
    return this.getParameterValue('partnerid');
  }

  public getAgentId(): string {
    return this.getParameterValue('agentid');
  }

  public getMemberId(): string {
    return this.getParameterValue('memberid');
  }

  public getThirdPartyId(): string {
    return this.getParameterValue('thirdpartyid');
  }

  public getThirdPartyGuid(): string {
    return this.getParameterValue('thirdpartyguid');
  }

  public getTransactionType(): string {
    return this.getParameterValue('transactiontype');
  }

  public getLoginToken(): string {
    return this.getParameterValue('token');
  }

  public getPlatform(): string {
    return this.getParameterValue('platform');
  }

  public getMemberClassId(): string {
    return this.getParameterValue('memberclassid');
  }

  public getParameterValue(name: string): string {
    const paramsStr = location.search.replace(/^\?/, '');
    const params = new HttpParams({
      fromString: paramsStr
    });
    const key = params.keys()
      .find(x => x.toLowerCase() === name.toLowerCase());
    const value = params.get(key);
    return value;
  }

  private setCorrelationId(correlationId: string) {
    if (!correlationId) {
      return;
    }

    // use sessionStorage not localStorage (browser tab) - TFS #34726
    sessionStorage.setItem(CORRELATION_ID, correlationId);
  }

  private getCorrelationId() {
    return sessionStorage.getItem(CORRELATION_ID)
  }
}

export function loader(configService: ConfigService) {
  return () => configService.loadConfig();
}

export function setAuthToken(token: string) {
  if (!token) {
    return;
  }

  // use sessionStorage not localStorage (browser tab) - TFS #34726
  sessionStorage.setItem(TOKEN_NAME, token);
}

export function getAuthToken() {
  const token = sessionStorage.getItem(TOKEN_NAME);
  return token;

}

export function getAuthTokenAndPrefix(token: string) {
  return `${AUTH_PREFIX} ${token}`;
}

export function setAuthTokenByHttpHeaders(headers: HttpHeaders): string {

  if (!headers || !headers.has(SET_AUTH_HEADER_KEY)) {
    return '';
  }

  const token = headers.get(SET_AUTH_HEADER_KEY);
  setAuthToken(token);

  return token;
}
